import React from "react";
import { Box, CssBaseline } from "@mui/material";
import {
  ThemeProvider,
  createTheme,
  alpha,
  ThemeOptions,
  PaletteColor,
} from "@mui/material/styles";
import { Navbar } from "./Components/Navbar";
import { HeroSection } from "./Components/HeroSection";
import { FeaturesSection } from "./Components/FeaturesSection";
import { WhyChooseUsSection } from "./Components/WhyChooseUsSection";
import { ContactFormSection } from "./Components/ContactFormSection";
import { Footer } from "./Components/Footer";

// Função auxiliar para obter a cor da paleta com segurança
const getPaletteColor = (
  palette: ThemeOptions["palette"],
  colorName: string | undefined
): PaletteColor | undefined => {
  if (!palette || !colorName) return undefined;
  // Mapeia as cores válidas da paleta
  const validColorKeys: (keyof typeof palette)[] = [
    "primary",
    "secondary",
    "error",
    "warning",
    "info",
    "success",
  ];
  if (validColorKeys.includes(colorName as keyof typeof palette)) {
    return palette[colorName as keyof typeof palette] as PaletteColor;
  }
  return undefined;
};

// Tema Modernizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#607D8B",
      dark: "#455A64",
      light: "#CFD8DC",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#8BC34A",
      dark: "#689F38",
      light: "#DCEDC8",
      contrastText: "#263238",
    },
    error: {
      main: "#D32F2F",
      dark: "#C62828",
    },
    info: {
      main: "#0288D1",
      dark: "#01579B",
    },
    background: {
      default: "#F7F9FA",
      paper: "#ffffff",
    },
    text: {
      primary: "#1a202c",
      secondary: "#546E7A",
    },
    divider: alpha("#000000", 0.1),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "3.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.8rem",
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
    },
    h3: { fontWeight: 600, fontSize: "1.8rem", lineHeight: 1.3 },
    h4: { fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.35 },
    h5: { fontWeight: 600, fontSize: "1.2rem", lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: "1.1rem", lineHeight: 1.4 },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
      color: "#3a4750",
    },
    body2: {
      fontSize: "0.9rem",
      lineHeight: 1.6,
      color: "#546E7A",
    },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.03em" },
    overline: {
      fontWeight: 600,
      letterSpacing: "0.1em",
      fontSize: "0.75rem",
      color: "#546E7A",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme: currentTheme }) => {
          const buttonColor = getPaletteColor(
            currentTheme.palette,
            ownerState.color
          );
          const mainColor =
            buttonColor?.main || currentTheme.palette.primary.main;

          return {
            borderRadius: 8,
            padding: "12px 28px",
            boxShadow:
              ownerState.variant === "contained"
                ? "0px 3px 8px rgba(0, 0, 0, 0.05)"
                : "none",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              boxShadow:
                ownerState.variant === "contained"
                  ? `0px 6px 20px ${alpha(mainColor, 0.25)}`
                  : "none",
              transform: "translateY(-2px)",
            },
          };
        },
        containedSecondary: ({ theme: currentTheme }) => ({
          color: currentTheme.palette.secondary.contrastText,
          "&:hover": {
            backgroundColor: currentTheme.palette.secondary.dark,
          },
        }),
        containedPrimary: ({ theme: currentTheme }) => ({
          "&:hover": {
            backgroundColor: currentTheme.palette.primary.dark,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation: 0,
        outlined: ({ theme: currentTheme }) => ({
          border: `1px solid ${currentTheme.palette.divider}`,
          boxShadow: "none",
        }),
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "transparent",
      },
      styleOverrides: {
        root: ({ theme: currentTheme }) => ({
          backgroundColor: alpha(currentTheme.palette.background.paper, 0.85),
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${currentTheme.palette.divider}`,
          color: currentTheme.palette.text.primary,
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "none",
      },
      styleOverrides: {
        root: ({ theme: currentTheme }) => ({
          color: currentTheme.palette.text.secondary,
          fontWeight: 500,
          transition: "color 0.3s ease-in-out",
          "&:hover": {
            color: currentTheme.palette.primary.main,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "medium",
      },
      styleOverrides: {
        root: ({ theme: currentTheme }) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: alpha(currentTheme.palette.common.white, 0.5),
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: currentTheme.palette.divider,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(currentTheme.palette.primary.main, 0.4),
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: currentTheme.palette.primary.main,
              borderWidth: "1px",
            },
          },
          "& .MuiInputLabel-root": {
            fontWeight: 500,
            color: currentTheme.palette.text.secondary,
            "&.Mui-focused": {
              color: currentTheme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: "lg",
      },
    },
    MuiIcon: {
      styleOverrides: {
        root: {},
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {},
      },
    },
  },
});

export const LandingPages0008 = () => {
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CssBaseline />
        <Navbar />
        <HeroSection />
        <FeaturesSection />
        <WhyChooseUsSection />
        <ContactFormSection />
        <Footer />
      </Box>
    </ThemeProvider>
  );
};
